import { useEffect, useState, useRef } from "react"
import React from "react"
import { TAbanUser, TAfindApprovalUser, TAverifyUser } from "../../../services/userAPI"
import ReadMore from "../../../components/ReadMore"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faVenus, faMars } from "@fortawesome/free-solid-svg-icons"
import { FilterValue } from "../../../types/FilterDatas"
import { selectDoApprovalUserFilters } from "../../../redux/store/filters/user/doApprovalUserFilterSlice"
import { SharedPost, TiktokVideo, User } from "../../../types/UserData"
import VideoPlayer from "../../../components/VideoPlayer"
import Filters from "./components/Filters"
import { numFixer } from "../getAllUsers/components/Functions"
import { useNavigate } from "react-router-dom"
import { isAbortError, getErrorMessage, getErrorStatus } from "../../../types/error"

const DoApprovalScreen = () => {
  const navigate = useNavigate()
  const doApprovalUserFilters = useSelector(selectDoApprovalUserFilters)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [data, setData] = useState<User[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  useEffect(() => {
    handleFetchData()
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const genderIcon = (gender: string) => {
    switch (gender) {
      case "male":
        return <FontAwesomeIcon icon={faMars} style={{ color: "#0000ff" }} />
      case "female":
        return <FontAwesomeIcon icon={faVenus} style={{ color: "#ff00dd" }} />
      default:
        return null
    }
  }

  const personalInfo: { key: string; value: any }[] = [
    { key: "İsim:", value: data[currentIndex]?.name },
    { key: "Doğum Tarihi:", value: data[currentIndex]?.birthday },
    { key: "Email:", value: data[currentIndex]?.email },
    { key: "Meslek:", value: data[currentIndex]?.job },
    { key: "Ülke:", value: data[currentIndex]?.country },
    { key: "Şehir:", value: data[currentIndex]?.city },
    { key: "Cinsiyet:", value: genderIcon(data[currentIndex]?.gender) },
    { key: "Hobiler:", value: data[currentIndex]?.hobbies?.join(", ") || "" },
  ]

  const instagramInfo: { key: string; value: any }[] = [
    { key: "Kullanıcı Adı:", value: data[currentIndex]?.insta?.username },
    { key: "Tam İsim:", value: data[currentIndex]?.insta?.full_name },
    { key: "Biyografi:", value: data[currentIndex]?.insta?.biography },
    { key: "Takipçi:", value: data[currentIndex]?.insta?.followers },
    { key: "Takip:", value: data[currentIndex]?.insta?.following },
    { key: "Gönderi Sayısı:", value: data[currentIndex]?.insta?.post_number },
    { key: "Ortalama Beğeni:", value: numFixer(data[currentIndex]?.insta?.average_like || 0) },
    { key: "Etkileşim Oranı:", value: numFixer(data[currentIndex]?.insta?.engagement_rate || 0) },
    { key: "Spam Durumu:", value: data[currentIndex]?.insta?.is_spam ? "True" : "False" },
    { key: "Görünürlük Durumu:", value: data[currentIndex]?.insta?.unvisible ? "True" : "False" },
  ]

  const tiktokInfo: { key: string; value: any }[] = [
    { key: "Kullanıcı Adı:", value: data[currentIndex]?.tiktok?.username },
    { key: "Takma Ad:", value: data[currentIndex]?.tiktok?.username },
    { key: "Takipçi:", value: data[currentIndex]?.tiktok?.followers },
    { key: "Takip:", value: data[currentIndex]?.tiktok?.following },
    { key: "Kalp Sayısı:", value: data[currentIndex]?.tiktok?.hearts },
    { key: "Ortalama Beğeni:", value: numFixer(data[currentIndex]?.tiktok?.tiktok_average_like) },
    {
      key: "Etkileşim Oranı:",
      value: numFixer(data[currentIndex]?.tiktok?.tiktok_engagement_rate),
    },
  ]

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    try {
      const flattenFilters = Object.entries(doApprovalUserFilters).reduce((acc, [key, filter]) => {
        const simpleKeys = [
          "gender",
          "verification",
          "warnings",
          "platform",
          "is_spam",
          "unvisible",
          "last_login",
          "frequency",
          "country",
          "city",
          "job",
          "insta_username",
          "tiktok_username",
        ]

        if (simpleKeys.includes(key) && typeof filter === "string") {
          acc[key] = filter
        } else if (key === "keywords" || key === "hobbies") {
          // Keywords and hobbies are handled separately below
        } else if (filter && typeof filter === "object") {
          const { min, max } = filter as FilterValue
          if (min) {
            acc[`min_${key}`] = min
          }
          if (max) {
            acc[`max_${key}`] = max
          }
        }

        return acc
      }, {} as { [key: string]: string })

      const params = new URLSearchParams(flattenFilters)

      const keywords = (doApprovalUserFilters.keywords as string[])?.map(
        (keyword) => keyword.charAt(0).toUpperCase() + keyword.slice(1),
      )
      keywords?.forEach((keywords) => {
        params.append("keywords", keywords)
      })

      const hobbies = (doApprovalUserFilters.hobbies as string[]).map(
        (hobby) => hobby.charAt(0) + hobby.slice(1),
      )
      hobbies.forEach((hobbies) => {
        params.append("hobbies", hobbies)
      })

      const response = await TAfindApprovalUser(
        params.toString(),
        abortControllerRef.current.signal,
      )
      if (response && Array.isArray(response)) {
        setData(response)
      } else {
        setError("No data found")
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setIsLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error fetching data")
    } finally {
      setIsLoading(false)
    }
  }

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex < data.length - 1 ? prevIndex + 1 : prevIndex))
  }

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex))
  }

  const handleApprove = async (id: string, status: string) => {
    setIsLoading(true)
    try {
      const response = await TAverifyUser(id, status)
      if (!response) {
        setError("Response is null")
        return
      }
      setIsLoading(false)
      if (currentIndex < data.length - 1) {
        handleNext()
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(getErrorMessage(error) || "No data found")
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBanUser = async (id: any) => {
    setIsLoading(true)
    try {
      handleApprove(id, "false")
      const response = await TAbanUser(id)
      if (!response) {
        setError("Response is null")
        return
      }
      setIsLoading(false)
      if (currentIndex < data.length - 1) {
        handleNext()
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("No data found")
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
      </div>
    )
  }

  if (data.length < 1) {
    return <div className="flex items-center justify-center h-40">Onay Bekleyen Kullanıcı Yok</div>
  }

  if (currentIndex >= data.length) {
    return <div className="flex items-center justify-center h-40">Geçersiz Kullanıcı İndeksi</div>
  }

  if (error) {
    return <p>Hata: {error}</p>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Filters
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        handleFetchData={handleFetchData}
      />

      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Onay Ekranı</h1>
            <p className="text-gray-600 mt-1">
              {data.length > 0
                ? `${currentIndex + 1} / ${data.length} kullanıcı`
                : "Kullanıcı bulunamadı"}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 lg:mt-0">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filtrele
            </button>
            <div className="flex gap-2">
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Geri
              </button>
              <button
                onClick={handleNext}
                disabled={currentIndex >= data.length - 1}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                İleri
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row p-6 gap-6">
        <div className="w-full lg:w-1/3">
          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Onay İşlemleri</h3>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleApprove(data[currentIndex]._id, "true")}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Onayla
              </button>
              <button
                onClick={() => handleApprove(data[currentIndex]._id, "false")}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Reddet
              </button>
              <button
                onClick={() => handleBanUser(data[currentIndex]._id)}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                  />
                </svg>
                Yasakla
              </button>
            </div>
          </div>
          {data.length > 0 && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-blue-100 text-blue-800 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </span>
                  Kişisel Bilgiler
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {personalInfo.map((info: { key: string; value: any }) => (
                    <div
                      key={info.key}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-sm font-medium text-gray-600">{info.key}</span>
                      <span className="text-sm text-gray-900 text-right max-w-xs truncate">
                        {info.value || "Belirtilmemiş"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instagram Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </span>
                  Instagram Bilgileri
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {instagramInfo.map((info: { key: string; value: any }) => (
                    <div
                      key={info.key}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-sm font-medium text-gray-600">{info.key}</span>
                      <span className="text-sm text-gray-900 text-right max-w-xs truncate">
                        {info.value || "Belirtilmemiş"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* TikTok Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-gray-100 text-gray-800 p-2 rounded-lg mr-3">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </span>
                  TikTok Bilgileri
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {tiktokInfo.map((info: { key: string; value: any }) => (
                    <div
                      key={info.key}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-sm font-medium text-gray-600">{info.key}</span>
                      <span className="text-sm text-gray-900 text-right max-w-xs truncate">
                        {info.value || "Belirtilmemiş"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="w-full lg:w-2/3 h-screen mt-4 lg:mt-0 p-4 lg:p-6">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Kullanıcı İçerikleri</h2>
            <p className="text-gray-600">{data[currentIndex]?.name} kullanıcısının paylaşımları</p>
          </div>

          {/* Instagram Posts Section */}
          {data.length > 0 &&
            data[currentIndex]?.insta?.shared_posts &&
            data[currentIndex]?.insta?.shared_posts.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm mr-2">
                    Instagram
                  </span>
                  Paylaşımlar ({data[currentIndex]?.insta?.shared_posts.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {data[currentIndex]?.insta?.shared_posts
                    .filter((post: SharedPost) => post && (post.id || post.media_url))
                    .map((post: SharedPost, index: number) => (
                      <div
                        key={post.id || `post-${index}`}
                        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100"
                      >
                        <div className="relative">
                          <img
                            className="h-48 w-full object-cover"
                            src={post.media_url}
                            alt={post.description || "Instagram post"}
                            onError={(e) => {
                              e.currentTarget.src = "/assets/images/placeholder-image.png"
                            }}
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                            #{index + 1}
                          </div>
                        </div>
                        <div className="p-4">
                          {post.location && post.location.name && (
                            <div className="font-semibold text-sm mb-2 text-blue-600 flex items-center">
                              📍 {post.location.name}
                            </div>
                          )}
                          <div className="mb-3">
                            {post.description && post.description.length > 100 ? (
                              <ReadMore content={post.description} maxCharacterCount={80} />
                            ) : (
                              <p className="text-gray-700 text-sm leading-relaxed">
                                {post.description || "Açıklama yok"}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium">
                              ❤️ {post.like_count || 0}
                            </span>
                            <span className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                              💬 {post.comment_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* TikTok Videos Section */}
          {data.length > 0 &&
            data[currentIndex]?.tiktok?.videos &&
            data[currentIndex]?.tiktok?.videos.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="bg-gradient-to-r from-black to-gray-800 text-white px-3 py-1 rounded-full text-sm mr-2">
                    TikTok
                  </span>
                  Videolar ({data[currentIndex]?.tiktok?.videos.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {data[currentIndex]?.tiktok?.videos
                    .filter((video: TiktokVideo) => video && (video.media_url || video.cover))
                    .map((video: TiktokVideo, index: number) => (
                      <div
                        key={video.media_url || `video-${index}`}
                        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100 cursor-pointer group"
                        onClick={() => {
                          if (video.media_url) {
                            setSelectedVideo(video.media_url)
                          }
                        }}
                      >
                        <div className="relative">
                          <img
                            className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                            src={video.cover}
                            alt={video.description || "TikTok video"}
                            onError={(e) => {
                              e.currentTarget.src = "/assets/images/placeholder-video.png"
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                            <div className="bg-white bg-opacity-90 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <svg
                                className="w-8 h-8 text-black"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                            #{index + 1}
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="mb-3">
                            {video.description && video.description.length > 100 ? (
                              <ReadMore content={video.description} maxCharacterCount={80} />
                            ) : (
                              <p className="text-gray-700 text-sm leading-relaxed">
                                {video.description || "Açıklama yok"}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium">
                              ❤️ {video.like_count || 0}
                            </span>
                            <span className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                              💬 {video.comment_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* No Content Message */}
          {data.length > 0 &&
            (!data[currentIndex]?.insta?.shared_posts ||
              data[currentIndex]?.insta?.shared_posts.length === 0) &&
            (!data[currentIndex]?.tiktok?.videos ||
              data[currentIndex]?.tiktok?.videos.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
                <div className="text-6xl mb-4">📱</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">İçerik Bulunamadı</h3>
                <p className="text-gray-600 text-center max-w-md">
                  Bu kullanıcının henüz Instagram paylaşımı veya TikTok videosu bulunmuyor.
                </p>
              </div>
            )}
        </div>
      </div>
      {selectedVideo && (
        <VideoPlayer selectedVideo={selectedVideo} onClose={() => setSelectedVideo("")} />
      )}
    </div>
  )
}

export default DoApprovalScreen
